import type SubscriptionEntity from '@thirsty-camel/hump-club/src/modules/subscription/subscription.entity'
import {
  Box,
  Button,
  Container,
  ContainerProps,
  Flex,
  Heading,
  IncrementInput,
  LinkButton,
  Price,
  Skeleton,
  Stack,
  StarRating,
  Text,
  Title,
  useToast,
  VisuallyHidden,
  useProductSubscriptionQuery,
  Icon,
  Divider,
} from '@thirstycamel/ui'
import type { CoreProductEntity } from '@ts/core/src/modules/coreproduct/coreproduct.entity'
import { ProductType } from '@ts/core/src/modules/coreproduct/coreproduct.types'
import React, { memo, useCallback, useEffect, useState } from 'react'
import { useApp } from '../../hooks/useApp'
import { useAuthHeaders } from '../../hooks/useAuthHeaders'
import { useUser } from '../../hooks/useUser'
import { useActions, useStore } from '../../store/hooks'
import { getNiceErrorMessage } from '../../utils/getNiceErrorMessage'
import ProductAvailability from '../ProductAvailability'
import ProductBadge from '../ProductBadge'
import ProductFavouriteStatus from '../ProductFavouriteStatus'
import ProductImagePreview from '../ProductImagePreview'
import { State } from '../../types/state'

export enum ProductSourceOptions {
  IN_STORE = 'IN_STORE',
  CLICK_AND_COLLECT = 'CLICK_AND_COLLECT',
  DELIVERY = 'DELIVERY',
}

export interface ProductSourceInStoreProps extends ContainerProps {
  product: CoreProductEntity
}

export const ProductSourceInStore = ({ product }: ProductSourceInStoreProps) => {
  const selectedStore = useStore(s => s.store.selectedStore)
  const { openPopout } = useActions(store => store.store)

  const isAvailable = !!selectedStore && product?.availabilityIndexes?.[0]?.available === true

  return (
    <Stack spacing={4}>
      {selectedStore && (
        <Stack spacing={4}>
          <Stack isInline>
            <Flex
              borderRadius="50%"
              borderWidth={1}
              borderColor={isAvailable ? 'green.500' : 'gray.500'}
              w="24px"
              h="24px"
              align="center"
              justify="center"
            >
              <Icon
                size="10px"
                name={isAvailable ? 'check' : 'close'}
                color={isAvailable ? 'green.500' : 'gray.500'}
              />
            </Flex>

            <Text color={!isAvailable ? 'gray.500' : 'green.500'}>
              <b>{isAvailable ? '' : 'Not '} Available Instore</b>
            </Text>
          </Stack>

          <ProductAvailability product={product} productSource={ProductSourceOptions.IN_STORE} />
        </Stack>
      )}

      {!selectedStore && (
        <Stack width={['100%', 'auto']}>
          <Text fontFamily="heading" fontSize="lg">
            To view in store availability and pricing for this product:
          </Text>
          <Button onClick={() => openPopout()} mt={5} variantColor="pink" rightIcon="place">
            Select a store
          </Button>
        </Stack>
      )}
    </Stack>
  )
}

export interface ProductSourceClickAndCollectProps extends ContainerProps {
  product: CoreProductEntity
}

export const ProductSourceClickAndCollect = ({ product }: ProductSourceClickAndCollectProps) => {
  const selectedStore = useStore(s => s.store.selectedStore)
  const { openPopout } = useActions(store => store.store)

  const isClickAndCollect = selectedStore?.isPickupReady
  const isAvailable = product?.availabilityIndexes?.[0]?.available === true

  const show = isClickAndCollect && isAvailable

  return (
    <Stack spacing={4}>
      {selectedStore && (
        <Stack spacing={4}>
          <Stack isInline>
            <Flex
              borderRadius="50%"
              borderWidth={1}
              borderColor={show ? 'green.500' : 'gray.500'}
              w="24px"
              h="24px"
              align="center"
              justify="center"
            >
              <Icon
                size="10px"
                name={show ? 'check' : 'close'}
                color={show ? 'green.500' : 'gray.500'}
              />
            </Flex>

            <Text color={!show ? 'gray.500' : 'green.500'}>
              <b>
                {!isClickAndCollect
                  ? 'This store does not offer Click and Collect'
                  : isAvailable
                  ? 'Available for Click and Collect'
                  : 'Not Available for Click and Collect'}
              </b>
            </Text>
          </Stack>

          <ProductAvailability
            product={product}
            productSource={ProductSourceOptions.CLICK_AND_COLLECT}
          />
        </Stack>
      )}

      {!selectedStore && (
        <Stack width={['100%', 'auto']}>
          <Text fontFamily="heading" fontSize="lg">
            To view click {'&'} collect availability and pricing for this product:
          </Text>
          <Button onClick={() => openPopout()} mt={5} variantColor="pink" rightIcon="place">
            Select a store
          </Button>
        </Stack>
      )}
    </Stack>
  )
}

export interface ProductSourceDeliveryProps extends ContainerProps {
  product: CoreProductEntity
}

export const ProductSourceDelivery = ({ product }: ProductSourceDeliveryProps) => {
  const selectedStore = useStore(s => s.store.selectedStore)
  const { openPopout } = useActions(store => store.store)

  const isDeliveryReady = selectedStore?.isDeliveryReady
  const isAvailable = product?.availabilityIndexes?.[0]?.available === true

  const show = isDeliveryReady && isAvailable

  return (
    <Stack spacing={4}>
      {selectedStore && (
        <Stack spacing={4}>
          <Stack isInline align="center">
            <Flex
              borderRadius="50%"
              borderWidth={1}
              borderColor={show ? 'green.500' : 'gray.500'}
              w="24px"
              h="24px"
              align="center"
              justify="center"
            >
              <Icon
                size="10px"
                name={show ? 'check' : 'close'}
                color={show ? 'green.500' : 'gray.500'}
              />
            </Flex>

            <Text color={!show ? 'gray.500' : 'green.500'}>
              <b>
                {!isDeliveryReady
                  ? 'This store does not offer Delivery'
                  : isAvailable
                  ? 'Available for Delivery'
                  : 'Not Available for Delivery'}
              </b>
            </Text>
          </Stack>

          <ProductAvailability product={product} productSource={ProductSourceOptions.DELIVERY} />
        </Stack>
      )}

      {!selectedStore && (
        <Stack width={['100%', 'auto']}>
          <Text fontFamily="heading" fontSize="lg">
            To view delivery availability and pricing for this product:
          </Text>
          <Button onClick={() => openPopout()} mt={5} variantColor="pink" rightIcon="place">
            Select a store
          </Button>
        </Stack>
      )}
    </Stack>
  )
}

export interface ProductSourceProps extends ContainerProps {
  product: CoreProductEntity
  // isFetching?: boolean
}

export const ProductSource = ({ product }: ProductSourceProps) => {
  const [selected, setSelected] = useState<ProductSourceOptions>(ProductSourceOptions.IN_STORE)

  return (
    <Stack
      // border="2px"
      // borderColor={product.id === variant.id ? 'pink.500' : 'transparent'}
      // borderColor={'pink.500'}
      // borderRadius="lg"
      w="100%"
      spacing={4}
    >
      <Stack isInline spacing={[0, 2, 2]}>
        <Button
          flex={['unset', 1, 1]}
          size="sm"
          variantColor={'pink'}
          variant={selected === ProductSourceOptions.IN_STORE ? 'solid' : 'outline'}
          onClick={() => setSelected(ProductSourceOptions.IN_STORE)}
          borderTopRightRadius={[0, '0.5rem', '0.5rem']}
          borderBottomRightRadius={[0, '0.5rem', '0.5rem']}
        >
          In Store
        </Button>

        <Button
          flex={['unset', 1, 1]}
          size="sm"
          variantColor={'pink'}
          variant={selected === ProductSourceOptions.CLICK_AND_COLLECT ? 'solid' : 'outline'}
          onClick={() => setSelected(ProductSourceOptions.CLICK_AND_COLLECT)}
          borderRadius={[0, '0.5rem', '0.5rem']}
        >
          Click {'&'} Collect
        </Button>

        <Button
          flex={['unset', 1, 1]}
          size="sm"
          variantColor={'pink'}
          variant={selected === ProductSourceOptions.DELIVERY ? 'solid' : 'outline'}
          onClick={() => setSelected(ProductSourceOptions.DELIVERY)}
          borderTopLeftRadius={[0, '0.5rem', '0.5rem']}
          borderBottomLeftRadius={[0, '0.5rem', '0.5rem']}
        >
          Delivery
        </Button>
      </Stack>

      <Stack
        // width={}
        p={4}
        border="2px"
        borderColor="gray.100"
        borderRadius="lg"
        flex={1}
      >
        {selected === ProductSourceOptions.IN_STORE && <ProductSourceInStore product={product} />}
        {selected === ProductSourceOptions.CLICK_AND_COLLECT && (
          <ProductSourceClickAndCollect product={product} />
        )}
        {selected === ProductSourceOptions.DELIVERY && <ProductSourceDelivery product={product} />}
      </Stack>
    </Stack>
  )
}

export default memo(ProductSource)
